import React, { useEffect, useMemo, useState } from "react";

import { useMutation } from "@apollo/client";
import {
  CameraFront as CameraFrontIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { Avatar, Box, MenuItem, Select, Typography } from "@mui/material";
import { isAfter } from "date-fns";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import {
  AppointmentListItemFragment,
  AppointmentStatusKind,
  UpdateAppointmentInput,
} from "@/gql/graphql";
import useFragment from "@/hooks/useFragment";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { theme } from "@/theme/customTheme";
import { colors } from "@/theme/overrides/colors";
import { format, parseDateAndTime } from "@/utils/datetime";
import { apply } from "@/utils/functions";
import { formatNational } from "@/utils/phone";

import OptionMenu from "./OptionMenu";
import StatusDotIndicator from "./StatusDotIndicator";
import { isAppointmentFinished } from "../utils/isAppointmentFinished";
import useAppointmentCurrent from "../utils/useAppointmentCurrent";

interface Props {
  id: string;
  onClickMenu: (type: string, id: string) => void;
  handleShowFinishModal: (
    data: AppointmentListItemFragment,
    status: AppointmentStatusKind
  ) => void;
  className?: string;
}

const useStyles = makeStyles()({
  item: {
    display: "flex",
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 12,
    paddingBottom: 12,
    backgroundColor: colors.white,
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      gap: 8,
    },
  },
  time: {
    fontSize: 16,
    fontWeight: 700,
    color: colors.gray,
  },
  col1: {
    minWidth: 200,
    paddingRight: 10,
    borderRightStyle: "solid",
    borderRightWidth: 2,
    borderRightColor: "#EDF0F5",
    [theme.breakpoints.down("md")]: {
      minWidth: 0,
      paddingRight: 0,
      paddingBottom: 8,
      borderRightWidth: 0,
      borderBottomStyle: "solid",
      borderBottomWidth: 2,
      borderBottomColor: "#EDF0F5",
    },
  },
  col2: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    paddingLeft: 20,
    [theme.breakpoints.down("md")]: {
      paddingLeft: 0,
    },
  },
  infoContainer: {
    display: "flex",
    flex: 1,
    alignItems: "center",
  },
  statusContainer: {
    marginTop: 4,
    display: "flex",
    alignItems: "center",
  },
  status: {
    marginLeft: 7,
    fontSize: 14,
    fontWeight: 600,
    textTransform: "capitalize",
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 50,
    backgroundColor: "#B8BEC9",
  },
  dotCurrent: {
    backgroundColor: "#FFD98A",
  },
  dotCancelled: {
    backgroundColor: "#FF6262",
  },
  dotUpcoming: {
    backgroundColor: "#2D87BB",
  },
  borderCurrent: {
    borderLeftWidth: 4,
    borderLeftColor: "#FFD98A",
    borderLeftStyle: "solid",
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 50,
    objectFit: "cover",
  },
  details: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    paddingLeft: 20,
  },
  name: {
    fontSize: 16,
    fontWeight: 700,
    color: "#26C9A7",
  },
  contactsContainer: {
    marginTop: 2,
    display: "flex",
    alignItems: "center",
  },
  typeIcon: {
    width: 16,
    marginRight: 5,
  },
  contacts: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.gray,
  },
});

const appointmentListItemFragment = graphql(`
  fragment AppointmentListItem on Appointment {
    id
    appointmentType
    client {
      id
      email
      profile {
        id
        fullName
        mobileNumber
        avatarUrl(variant: THUMB)
      }
    }
    status
    startDate
    startTime
    endDate
    endTime
    meetingDetails {
      roomUrl
    }
  }
`);

const updateAppointmentStatusMutation = graphql(`
  mutation UpdateAppointmentStatus($input: UpdateAppointmentInput!) {
    updateAppointment(input: $input) {
      appointment {
        id
      }
    }
  }
`);

const AppointmentListItem: React.FC<Props> = ({
  id,
  onClickMenu,
  handleShowFinishModal,
  className,
}) => {
  const { classes, cx } = useStyles();
  const snackbar = useGlobalSnackbar();
  const [updateAppointmentStatus] = useMutation(
    updateAppointmentStatusMutation
  );
  const [appointment] = useFragment(
    appointmentListItemFragment,
    `Appointment:${id}`
  );
  const isCurrentAppointmentDate = useAppointmentCurrent(appointment);

  const isAppointmentActive =
    appointment?.status !== "cancelled" && !isAppointmentFinished(appointment);
  const isOnline = appointment?.appointmentType === "online";
  const endDateTime = useMemo(
    () =>
      parseDateAndTime(
        appointment?.endDate ?? new Date(),
        appointment?.endTime ?? new Date()
      ),
    [appointment]
  );
  const hasAppointmentEndTimePassed = isAfter(new Date(), endDateTime);
  const startTime = apply(appointment?.startTime, (timeofday) =>
    format({ timeofday }, "hh:mm a")
  );
  const endTime = apply(appointment?.endTime, (timeofday) =>
    format({ timeofday }, "hh:mm a")
  );

  const status = ((): string | null | undefined => {
    if (appointment?.status == "upcoming" && isCurrentAppointmentDate) {
      return "CURRENT";
    } else if (isAppointmentFinished(appointment)) {
      return AppointmentStatusKind.Finished;
    } else {
      return appointment?.status?.toUpperCase();
    }
  })();

  useEffect(() => {
    if (status) {
      setCurrentStatus(status as AppointmentStatusKind);
    }
  }, [status]);

  useEffect(() => {
    if (appointment?.status === "upcoming" && hasAppointmentEndTimePassed) {
      handleUpdateAppointmentStatusToFinished();
    }
  }, [status]);

  const handleUpdateAppointmentStatusToFinished = async () => {
    try {
      const input: UpdateAppointmentInput = {
        id: appointment?.id ?? "",
        patch: {
          status: AppointmentStatusKind.Finished,
        },
      };

      await updateAppointmentStatus({
        variables: { input: input },
      });
    } catch (error) {
      snackbar.open(`There was an error in updating your appointment`);
    }
  };

  const [currentStatus, setCurrentStatus] = useState<AppointmentStatusKind>(
    status as AppointmentStatusKind
  );

  const handleChange = () => setCurrentStatus(status as AppointmentStatusKind);

  return (
    <Box
      className={cx(classes.item, className, {
        [classes.borderCurrent]: status == "CURRENT",
      })}
    >
      <Box className={classes.col1}>
        <Typography variant="body1" className={classes.time}>
          {startTime}- {endTime}
        </Typography>
        <Box className={classes.statusContainer}>
          <StatusDotIndicator status={status ?? "CURRENT"} />
          <Select
            value={currentStatus}
            onChange={handleChange}
            color="primary"
            variant="standard"
            disableUnderline
            disabled={
              currentStatus == AppointmentStatusKind.Finished ||
              currentStatus == AppointmentStatusKind.Cancelled
            }
          >
            <MenuItem
              value={AppointmentStatusKind.Upcoming}
              style={{ display: "none" }}
            >
              Upcoming
            </MenuItem>
            <MenuItem
              value={AppointmentStatusKind.Cancelled}
              onClick={() => {
                handleShowFinishModal(
                  appointment!,
                  AppointmentStatusKind.Cancelled
                );
              }}
            >
              Cancelled
            </MenuItem>

            <MenuItem value="CURRENT" style={{ display: "none" }}>
              Current
            </MenuItem>

            <MenuItem
              value={"FINISHED"}
              onClick={() => {
                handleShowFinishModal(
                  appointment!,
                  AppointmentStatusKind.Finished
                );
              }}
            >
              Finished
            </MenuItem>
          </Select>
        </Box>
      </Box>
      <Box className={classes.col2}>
        <Box className={classes.infoContainer}>
          <Avatar
            className={classes.avatar}
            src={appointment?.client.profile?.avatarUrl ?? undefined}
            alt="avatar"
          />
          <Box className={classes.details}>
            <Typography className={classes.name}>
              {appointment?.client.profile?.fullName}
            </Typography>
            <Box className={classes.contactsContainer}>
              {isOnline ? (
                <CameraFrontIcon
                  className={classes.typeIcon}
                  htmlColor={colors.gray}
                />
              ) : (
                <PersonIcon
                  className={classes.typeIcon}
                  htmlColor={colors.gray}
                />
              )}
              <Typography variant="body2" className={classes.contacts}>
                {[
                  isOnline ? "Online Appointment" : "In-person Appointment",
                  appointment?.client.email,
                  apply(
                    appointment?.client.profile?.mobileNumber,
                    formatNational
                  ),
                ]
                  .filter(Boolean)
                  .join(" | ")}
              </Typography>
            </Box>
          </Box>
          <Box>
            <OptionMenu
              onClickMenu={(type) => onClickMenu(type, id)}
              isAppointmentActive={isAppointmentActive}
              isOnlineAppointment={isOnline}
              meetingLink={appointment?.meetingDetails?.roomUrl}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AppointmentListItem;
